import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setActiveCheck, clearActiveCheck, setJobStarted } from "../../../../store/genaiRightsSlice";
import { TopNav, SideNav } from "../../navigation/ConsoleNavigation";
import GetCookie from "../../../utils/GetCookie";
import authNavigate from "../../../globalComponents/AuthNav";
import ProcessingPopup from "../../../utils/popup/ProcessingPopup";
import ErrorPopup from "../../../utils/popup/ErrorPopup";
import SuccessPopup from "../../../utils/popup/SuccessPopup";
import GenAIRightsList from "./GenAIRightsList";
import GenAIRightsDashboard from "./GenAIRightsDashboard";
import CreateGenAIRightsCheckModal from "./CreateGenAIRightsCheckModal";

const API_BASE_URL = "/pceapi/slr/genai-rights";

const GenAIRights = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { activeCheck } = useSelector((state) => state.genaiRights);

    const [checks, setChecks] = useState([]);
    const [total, setTotal]   = useState(0);
    const [page, setPage]     = useState(1);
    const [size, setSize]     = useState(25);
    const [search, setSearch] = useState("");
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const createBtnWrapperRef = useRef(null);

    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [popupErrorMessage, setPopupErrorMessage] = useState("");
    const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

    const headers = useMemo(() => ({
        Authorization: GetCookie("access_token"),
    }), []);

    const fetchChecks = async () => {
        try {
            setIsProcessingPopupVisible(true);
            setProcessingMessage("Loading rights checks...");

            const response = await axios.post(
                `${API_BASE_URL}/list`,
                { page, size, search },
                { headers }
            );

            if (response.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (response.data?.status === "success") {
                setChecks(response.data.data?.checks || []);
                setTotal(response.data.data?.total || 0);
            } else {
                setPopupErrorMessage(response.data?.message || "Failed to load rights checks");
            }
        } catch (error) {
            if (error.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(
                error.response?.data?.message || error?.message || "Failed to load rights checks"
            );
        } finally {
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const handleDeleteCheck = async (checkObj) => {
        const res = await axios.post(
            `${API_BASE_URL}/delete`,
            { job_id: checkObj.id },
            { headers }
        );

        if (res.data?.status === "NotAuthorized") {
            authNavigate("/login", "iam", navigate);
            return;
        }
        if (res.data?.status !== "success") {
            throw new Error(res.data?.message || "Failed to delete check");
        }
        setRefreshTrigger((p) => p + 1);
    };

    /* A new check was just created — go straight into its dashboard so the
       user watches it run rather than hunting for it in the list. */
    const handleCreateSuccess = (data) => {
        setIsCreateOpen(false);
        dispatch(setJobStarted({
            jobId:     data.job_id,
            name:      data.name,
            totalRows: data.total_rows,
        }));
        setPage(1);
        setRefreshTrigger((prev) => prev + 1);
    };

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (createBtnWrapperRef.current && !createBtnWrapperRef.current.contains(e.target)) {
                setIsCreateOpen(false);
            }
        };
        if (isCreateOpen) document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [isCreateOpen]);

    useEffect(() => {
        if (!activeCheck) {
            fetchChecks();
        }
    }, [page, size, search, refreshTrigger, activeCheck]);

    return (
        <div className="main-window">
            <TopNav />

            <div className="container-fluid">
                <div className="row">
                    <SideNav />

                    <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
                        <div className="">
                            <div className="ev-card-2 dashboard-header position-static mb-3 w-100">
                                <div></div>

                                <p className="ev-med-p-dark mb-0 ev-bold-reg">
                                    GenAI Rights
                                </p>

                                <div
                                    ref={createBtnWrapperRef}
                                    className="position-relative"
                                    style={{ zIndex: isCreateOpen ? 1050 : 1 }}
                                >
                                    <button
                                        type="button"
                                        className="ev-btn-yellow-fill-round ev-btn-reg ev-bold-reg"
                                        onClick={() => setIsCreateOpen((prev) => !prev)}
                                    >
                                        <i className="bi bi-plus-circle me-1"></i> New Rights Check
                                    </button>

                                    {isCreateOpen && (
                                        <CreateGenAIRightsCheckModal
                                            onClose={() => setIsCreateOpen(false)}
                                            onSuccess={handleCreateSuccess}
                                        />
                                    )}
                                </div>
                            </div>

                            {activeCheck ? (
                                <GenAIRightsDashboard
                                    check={activeCheck}
                                    onBack={() => {
                                        dispatch(clearActiveCheck());
                                        setRefreshTrigger((p) => p + 1);
                                    }}
                                />
                            ) : (
                                <div className="m-4">
                                    <p className="ev-med-p-dark-blue mb-2">
                                        Rights Checks ({total})
                                    </p>
                                    <GenAIRightsList
                                        checks={checks}
                                        total={total}
                                        page={page}
                                        setPage={setPage}
                                        size={size}
                                        setSize={setSize}
                                        search={search}
                                        setSearch={setSearch}
                                        onRefresh={() => setRefreshTrigger((p) => p + 1)}
                                        onOpenDashboard={(row) => dispatch(setActiveCheck(row))}
                                        onDeleteCheck={handleDeleteCheck}
                                    />
                                </div>
                            )}
                        </div>
                    </main>
                </div>
            </div>

            {isProcessingPopupVisible && (
                <ProcessingPopup message={processingMessage || "Processing..."} />
            )}

            {popupErrorMessage && (
                <ErrorPopup onClose={() => setPopupErrorMessage("")} message={popupErrorMessage} />
            )}

            {popupSuccessMessage && (
                <SuccessPopup onClose={() => setPopupSuccessMessage("")} message={popupSuccessMessage} />
            )}
        </div>
    );
};

export default GenAIRights;